/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form"
import LayoutSidebar from "../../../components/LayoutSidebar";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../redux/userSlice";
import { selectAuth } from "../../../redux/authSlice";
import { selectPortfolios } from "../../../redux/portfolioSlice";
import {
  faFilePdf,
  faMagnifyingGlass,
  faMoneyCheckDollar,
  faTrashCan,
  faEye,
  faQuestionCircle,
  faPencil
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { setModal } from '../../../redux/modalSlice'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDropzone } from "react-dropzone";
import { useRequest } from "../../../hooks/UseRequest";
import { setNotification } from "../../../redux/notificationSlice";
import Loading from "../../../components/Loading";
import { hasUserPermission } from "../../../utils/permissionHelper";
import { useNavigate, useParams } from "react-router-dom";
import { selectDropdownOptions, selectDropdowns } from '../../../redux/dropdownSlice'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'

// todo: support deleting multiple rows at once and allow submitting multiple rows at once after inital row submitted with batchid returned

const BulkCheckEntry = () => {
  const [selectedPortfolio, setSelectedPortfolio] = useState()
  const [currentDropdownSelection, setCurrentDropdownSelection] = useState(null)
  const dispatch = useDispatch();
  const requests = useRequest()
  const [batchID, setBatchID] = useState(null);
  const [lockedRows, setLockedRows] = useState([]);
  const [rowSaving, setRowSaving] = useState([]);
  const [rowDeleting, setRowDeleting] = useState([]);
  const [validPortfolios, setValidPortfolios] = useState([]);
  const [isVerified, setIsVerified] = useState(false)
  const [batchSaving, setBatchSaving] = useState(false);
  const navi = useNavigate()
  const params = useParams()
  const paymentCheckBatchId = params?.id
  const isEditMode = Boolean(paymentCheckBatchId);

  const dropdownOptions = useSelector(selectDropdownOptions)
  const dropdowns = useSelector(selectDropdowns)
  const processingStatusVerifyDDOId = dropdownOptions.find((x) => x?.code_key === 'processing_status_verify')?.id
  const processingStatusPendingDDOId = dropdownOptions.find((x) => x?.code_key === 'processing_status_pending')?.id
  const [processingStatusId, setProcessingStatusId] = useState(processingStatusVerifyDDOId)

  const checkTypeOptions = useMemo(() => {
    const checkTypeDropdown = dropdowns.data?.find((o) => o.code_key === 'check_type')

    return dropdownOptions
      .filter((o) => o.dropdown_id === checkTypeDropdown?.id && o.is_active === 1)
      .map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }))
  }, [dropdowns.data, dropdownOptions])

  const user = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const portfolios = useSelector(selectPortfolios)
  const today = new Date().toISOString().split("T")[0];

  const { trigger, watch, handleSubmit, register, control, reset, setError, clearErrors, formState: { errors }, setValue } = useForm({
    defaultValues: {
      deposit_slip_amount: null,
      deposit_date: null,
      batch_image: null,
      slip_image: null,
      checkEntry: [{
        id: null,
        customer_name: '',
        customer_id: '',
        check_amount: null,
        check_number: '',
        postmarked_date: '',
        loan_number: '',
        check_type_dropdown_option_id: null,
        check_image: '',
        check_s3_link: '',
        envelope_image: '',
        envelope_s3_link: '',
      }]
    }
  });

  const defaultCheckEntryValues = {
    customer_name: '',
    customer_id: '',
    check_amount: null,
    check_number: '',
    postmarked_date: '',
    loan_number: '',
    check_type_dropdown_option_id: null,
    check_image: '',
    envelope_image: ''
  }
  const { remove, fields, append, update } = useFieldArray({
    name: 'checkEntry',
    control
  })
  const checkEntryWatch = useWatch({
    name: "checkEntry",
    control
  });
  const batchImageWatch = watch('batch_image');
  const slipImageWatch = watch('slip_image');
  const slipTotalWatch = watch('deposit_slip_amount');
  const depositDateWatch = watch('deposit_date');

  useEffect(() => {
    if (user.status === 'succeeded' && user.data.user_permission_options) {
      const activePermissions = user.spoofed_user.user_permission_options ? user.spoofed_user.user_permission_options : user.data.user_permission_options
      if (!hasUserPermission(activePermissions, `payment_check_batch_create`)) {
        navi('/403')
      }
    }
  }, [user])

  useEffect(() => { setIsVerified(false); }, [checkEntryWatch, slipTotalWatch, slipImageWatch, batchImageWatch])

  const canPerformRowAction = () => {
    return (rowSaving.length === 0 && rowDeleting.length === 0)
  }

  const getPaymentCheckBatch = async (id) => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_check_batch/${id}`)
      const response = await requests.get();
      setLockedRows([]);
      if (response.status === 200) {
        if (response.data.data.processing_status_dropdown_option_id != processingStatusVerifyDDOId) {
          navi('/payment_check_batch')
        }
        setSelectedPortfolio(response.data.data.portfolio)

        // Fill up the form
        setValue('deposit_slip_amount', response.data.data.deposit_slip_amount)
        setValue('deposit_date', response.data.data.deposit_date)
        setValue('batch_image', response.data.data.batch_s3_link)
        setValue('slip_image', response.data.data.slip_s3_link)

        if (response.data.data.check_batch_entries.length > 0) {
          response.data.data.check_batch_entries.forEach((entry, index) => {
            setLockedRows((rows) => rows.concat(entry.id))

            update(index, {
              ...checkEntryWatch[index],
              id: entry.id,
              customer_name: entry.customer.label,
              customer_id: entry.customer.id,
              payment_check_entry: entry.id,
              check_amount: entry.amount,
              check_number: entry.check_number,
              postmarked_date: entry.postmarked_date,
              loan_number: entry.loan?.label ?? "",
              check_type_dropdown_option_id: entry.check_type_dropdown_option_id,
              check_image: entry.check_s3_link,
              envelope_image: entry.envelope_s3_link
            });
          });
        }
      }
    } catch (error) {
      setLockedRows([]);
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const updateLockRows = (id) => {
    // delete specific id
    setLockedRows((rows) => rows.filter((i) => i !== id))
  }

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      if (isEditMode) {
        // fetch batch data
        setBatchID(paymentCheckBatchId)
        getPaymentCheckBatch(paymentCheckBatchId)
      } else if (portfolios.data.length > 0) {
        setValidPortfolios(portfolios.data.filter((p) => p.name !== "All Portfolios" && p.is_active));
        if (user.data.limit_to_portfolio_id) {
          let limitedPortfolioIds = user.data.limit_to_portfolio_id;
          let limitedPortfolio = []
          limitedPortfolioIds.forEach((portfolioId) => {
            let portfolioData = portfolios.data.filter((portfolio) => portfolio.id === portfolioId);
            portfolioData = portfolioData[0] ?? null
            if (portfolioData) {
              limitedPortfolio.push(portfolioData)
            }
          })
          if (limitedPortfolio.length === 1) {
            setSelectedPortfolio(limitedPortfolio[0])
          }
        } else if (user.filtered_portfolio) {
          const nameFiltered = portfolios.data.find((portfolio) => portfolio.id === user.filtered_portfolio)
          setSelectedPortfolio(nameFiltered)
        }
      }
    }
  }, [auth.accessToken, portfolios, paymentCheckBatchId])

  if (!portfolios || !user) {
    return null
  }

  const handleSelectPortfolio = () => {
    setSelectedPortfolio(validPortfolios[currentDropdownSelection])
  }

  const convertToBase64 = (file, cb) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      cb(reader.result)
    };
  }

  const handleFormSubmit = async (vals) => {
    setBatchSaving(true);

    let body = {
      batch_image: vals.batch_image,
      slip_image: vals.slip_image,
      deposit_slip_amount: vals.deposit_slip_amount,
      deposit_date: vals.deposit_date,
      processing_status_dropdown_option_id: processingStatusId,
    }

    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_check_batch/${batchID}`)
      await requests.modify(body);
      dispatch(setNotification({ type: 'success', body: "The check batch has been submitted successfully" }))
      setLockedRows([]);
      setBatchSaving(false);
      setIsVerified(false);
      setBatchID(null);
      reset();
      navi('/bulk_check_entry')
      append([defaultCheckEntryValues, defaultCheckEntryValues, defaultCheckEntryValues, defaultCheckEntryValues]);
    } catch (error) {
      setBatchSaving(false);
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleSearchLoan = async (val, index) => {
    try {
      setRowSaving(rowSaving.concat(index));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan?filters=${JSON.stringify({ and: [{ column: "guid", value: val, operator: "=" }] })}`);
      const results = await requests.get();
      if (results?.data?.data[0]) {
        clearErrors(`checkEntry.${index}.loan_number`)
        update(index, {
          ...checkEntryWatch[index],
          customer_name: results.data.data[0].customer_label,
          customer_id: results.data.data[0].customer_id,
        });
      } else {
        setError(`checkEntry.${index}.loan_number`, { type: "manual", message: "No loan with that number found" })
      }
      setRowSaving(rowSaving.filter((i) => i !== index));
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.errors }))
    }
  }

  const handleVerify = async () => {
    setIsVerified(true);
    if (!slipImageWatch || !slipTotalWatch || !depositDateWatch) {
      if (!slipImageWatch) {
        setError(`slip_image`, { type: "manual", message: "Slip image is required" })
      }
      if (!slipTotalWatch) {
        setError(`deposit_slip_amount`, { type: "manual", message: "Deposit slip total is required" })
      }
      if (!depositDateWatch) {
        setError(`deposit_date`, { type: "manual", message: "Deposit date is required" })
      }
      setIsVerified(false);
      dispatch(setNotification({ type: 'error', body: "Mandatory fields are missing" }))
      return;
    }

    let rowsToRemove = [];
    let hasError = false;
    checkEntryWatch.forEach((entry, index) => {
      if (!entry.payment_check_entry) {
        if (entry.customer_name || entry.check_amount || entry.check_number || entry.postmarked_date || entry.check_type_dropdown_option_id || entry.check_image) {
          trigger([`checkEntry.${index}.customer_name`, `checkEntry.${index}.check_amount`, `checkEntry.${index}.check_number`, `checkEntry.${index}.postmarked_date`, `checkEntry.${index}.check_type_dropdown_option_id`, `checkEntry.${index}.check_image`, `checkEntry.${index}.envelope_image`]);
        }
        setIsVerified(false);
        dispatch(setNotification({ type: 'error', body: "You must save or delete check entry rows before verifying and submitting the batch" }))
        hasError = true;
      }
    })
    if (hasError) return;

    let amount = 0;

    checkEntryWatch.forEach((entry) => {
      if (entry.payment_check_entry) {
        amount += parseFloat(entry.check_amount);
      }
    })
    if (parseFloat(amount).toFixed(2) === parseFloat(slipTotalWatch).toFixed(2)) {
      setIsVerified(true);
    } else {
      setIsVerified(false);
      setError(`deposit_slip_amount`, { type: "manual", message: "Slip Total does not match" })
    }
  }

  const handleRowDelete = async (index, payment_check_entry) => {
    if (checkEntryWatch[index].payment_check_entry && batchID) {
      setRowDeleting((rows) => rows.concat(checkEntryWatch[index].payment_check_entry));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_check_entry/${checkEntryWatch[index].payment_check_entry}/payment_check_batch/${batchID}/portfolio/${selectedPortfolio.id}`);
      try {
        await requests.delete();
        setRowDeleting((rows) => rows.filter((i) => i !== checkEntryWatch[index].payment_check_entry));
        setLockedRows((lRows) => lRows.filter((i) => i !== index))
        remove(checkEntryWatch.findIndex((i) => i.payment_check_entry === payment_check_entry));
      } catch (error) {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.errors }))
      }
    } else {
      remove(index);
    }
  }

  useEffect(() => {
    append([defaultCheckEntryValues, defaultCheckEntryValues]);
  }, [])

  const saveRow = async (index) => {
    const data = { ...checkEntryWatch[index] }
    setRowSaving((rows) => { return rows.concat(index) });
    trigger([`checkEntry.${index}.customer_name`, `checkEntry.${index}.check_amount`, `checkEntry.${index}.check_number`, `checkEntry.${index}.postmarked_date`, `checkEntry.${index}.check_type_dropdown_option_id`, `checkEntry.${index}.check_image`, `checkEntry.${index}.envelope_image`])
    if (!data.customer_name || !data.check_amount || !data.check_number || !data.postmarked_date || !data.check_type_dropdown_option_id || !data.check_image) { setRowSaving(rowSaving.filter((i) => i !== index)); return }
    if (errors?.checkEntry?.[index]) {
      if (errors.checkEntry[index].loan_number) {
        clearErrors(`checkEntry.${index}.loan_number`)
      }
    }
    if (!errors?.checkEntry || (errors?.checkEntry?.[index] && errors?.checkEntry?.[index].length === 0)) {
      try {
        requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_check_entry/store_entry`);
        const response = await requests.post({ ...(batchID && { payment_check_batch_id: batchID }), portfolio_id: selectedPortfolio.id, ...data })
        if (response.status === 200 || response.status === 201) {
          setLockedRows((rows) => rows.concat(response.data.data.id))
          setRowSaving((rows) => rows.filter((i) => i !== index));
          update(index, {
            ...checkEntryWatch[index],
            id: response.data.data.id,
            payment_check_entry: response.data.data.id,
          });
          if (!batchID) {
            setBatchID(response.data.data.payment_check_batch_id)
          }
        } else {
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
          setRowSaving(rowSaving.filter((i) => i !== index));
        }
      } catch (error) {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
        setRowSaving(rowSaving.filter((i) => i !== index));
      }
    }
  }

  const baseStyle = {
    borderColor: "black",
    width: "60px",
    height: "40px",
    borderStyle: "dashed",
    backgroundColor: "rgb(235, 239, 242)",
    margin: "0 auto"
  }

  const focusedStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: '#00e676',
    backgroundColor: '#00e676',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
    backgroundColor: '#ff1744',
  };

  const buildDropzone = (field, triggerString, clearError = false) => {
    const onDrop = useCallback((acceptedFiles) => {
      convertToBase64(acceptedFiles[0], (b64) => {
        field.field.onChange(b64);
        if (clearError) {
          clearErrors(triggerString)
        } else {
          trigger([triggerString])
        }
      })
    }, [])
    const { getRootProps, getInputProps, isDragActive, isFocused, isDragReject, isDragAccept } = useDropzone({ onDrop })

    const style = useMemo(() => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }), [
      isFocused,
      isDragAccept,
      isDragReject
    ]);

    return (
      <div
        className="is-flex is-justify-content-center is-size-4 fa-border is-align-items-center"
        {...getRootProps({ style })}
      >
        <input {...getInputProps()} />
        <div className="is-flex is-justify-content-center">
          <FontAwesomeIcon icon={faMoneyCheckDollar} />
        </div>
      </div>
    )
  }

  if (selectedPortfolio) {
    return (
      <LayoutSidebar
        contentTitle={`Check Entry Form - ${selectedPortfolio.name}`}
        actionButtons={(
          <button
            type="button"
            className="button has-background-grey has-text-white is-small"
            onClick={() => {
              navi('/payment_check_batch')
            }}
          >
            <span className="mx-3">
              Payment Check Batches
            </span>
          </button>
        )}
        content={(
          <div>
            <form onSubmit={handleSubmit(handleFormSubmit)}>

              <div className="lox-detail mt-3">

                <table className="table lox-hooktable-table table is-bordered is-striped has-sticky-column">
                  <thead>
                    <tr>
                      <th className="has-text-centered is-selected sticky" style={{ width: "200px" }}>
                        Name <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Check Amount <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Check Number <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Postmarked Date <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Loan Number
                      </th>
                      <th className="has-text-centered is-selected" style={{ width: "300px" }}>
                        Check Type <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Check Image <span className="has-text-danger">*</span>
                      </th>
                      <th className="has-text-centered is-selected">
                        Envelope Image
                      </th>
                      <th className="has-text-centered is-selected">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => {
                      return (
                        <tr key={field.id} className="has-text-centered">

                          <td>
                            <a
                              className="mt-2"
                              onClick={() => {
                                window.open(`/customer/${checkEntryWatch?.[index]?.customer_id}`, "_blank")
                              }}
                            >{checkEntryWatch?.[index]?.customer_name}</a>
                            {
                              (lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving?.indexOf(index) >= 0) || batchSaving) ?
                                null : (
                                  <Controller
                                    name={`checkEntry.${index}.customer_name`}
                                    control={control}
                                    rules={{ required: true }}
                                    render={(field) => (
                                      <a
                                        className="is-primary pl-1 is-size-5"
                                        onClick={() => {
                                          dispatch(
                                            setModal({
                                              is_active: true,
                                              type: 'customer_search_check_entry_modal',
                                              options: {
                                                portfolio_id: selectedPortfolio.id,
                                                successCallback: (val) => {
                                                  update(index, {
                                                    ...checkEntryWatch[index],
                                                    customer_name: val.name,
                                                    customer_id: val.id,
                                                  });
                                                  trigger([`checkEntry.${index}.customer_id`, `checkEntry.${index}.customer_name`])
                                                }
                                              }
                                            })
                                          )
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                      </a>
                                    )}
                                  />
                                )
                            }
                            {errors?.checkEntry?.[index]?.customer_name && (
                              <p
                                className="has-text-danger is-size-7 lox-capitalize-first-letter"
                              >
                                {(errors.checkEntry[index].customer_name.message || 'Customer Name is required')}

                              </p>
                            )}

                            <input type="hidden" {...register(`checkEntry.${index}.payment_check_entry`)} />
                          </td>
                          <td>
                            <input
                              className="input is-small"
                              type="number"
                              step="0.01"
                              disabled={(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving.indexOf(index) >= 0) || batchSaving)}
                              {...register(`checkEntry.${index}.check_amount`, {
                                required: true
                              })}
                              onBlur={() => {
                                trigger([`checkEntry.${index}.check_amount`])
                              }}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.check_amount && (errors.checkEntry[index].check_amount.message || 'Check amount is required')}
                            </p>
                          </td>
                          <td>
                            <input
                              className="input is-small"
                              type="text"
                              disabled={(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving.indexOf(index) >= 0) || batchSaving)}
                              {...register(`checkEntry.${index}.check_number`, {
                                required: true
                              })}
                              onBlur={() => {
                                trigger([`checkEntry.${index}.check_number`])
                              }}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.check_number && (errors.checkEntry[index].check_number.message || 'Check number is required')}
                            </p>
                          </td>
                          <td>
                            <input
                              className="input is-small"
                              disabled={(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving.indexOf(index) >= 0) || batchSaving)}
                              type="date"
                              {...register(`checkEntry.${index}.postmarked_date`, {
                                required: true
                              })}
                              onBlur={() => {
                                trigger([`checkEntry.${index}.postmarked_date`])
                              }}
                              max={today}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.postmarked_date && (errors.checkEntry[index].postmarked_date.message || 'Postmarked date is required')}
                            </p>
                          </td>
                          <td>
                            <input
                              className="input is-small"
                              type="text"
                              disabled={(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving.indexOf(index) >= 0) || batchSaving)}
                              {...register(`checkEntry.${index}.loan_number`, {
                                required: false
                              })}
                              onBlur={() => {
                                if (checkEntryWatch?.[index]?.loan_number) {
                                  handleSearchLoan(checkEntryWatch?.[index]?.loan_number, index)
                                }
                              }}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.loan_number && (errors.checkEntry[index].loan_number.message || 'Loan Number Error')}
                            </p>
                          </td>
                          <td>
                            <Controller
                              name={`checkEntry.${index}.check_type_dropdown_option_id`}
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <Select
                                  value={checkTypeOptions.find((o) => o.value === field.value) ?? null}
                                  onChange={(option) => field.onChange(option.value)}
                                  options={checkTypeOptions}
                                  isDisabled={(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || (rowSaving.indexOf(index) >= 0) || batchSaving)}
                                  classNames={reactSelectClassNames({ check_type: errors?.checkEntry?.[index]?.check_type_dropdown_option_id }, 'check_type')}
                                  isSearchable={false}
                                />
                              )}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.check_type_dropdown_option_id && (errors.checkEntry[index].check_type_dropdown_option_id.message || 'Check type is required')}
                            </p>
                          </td>
                          <td>
                            {/*eslint-disable-next-line no-nested-ternary*/}
                            {checkEntryWatch?.[index]?.check_image ?

                              (checkEntryWatch?.[index]?.check_image.startsWith('data:image') ? (
                                <img
                                  style={{ width: "30px", height: "30px" }}
                                  src={checkEntryWatch?.[index]?.check_image}
                                />
                              ) : checkEntryWatch?.[index]?.check_image.startsWith('http') ? (
                                <div style={{ paddingBottom: '0.2rem' }}>
                                  <a
                                    className="button is-primary"
                                    href={checkEntryWatch?.[index]?.check_image}
                                    style={{
                                      height: '2rem',
                                      width: '2rem',
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </a>
                                </div>
                              ) :
                                <FontAwesomeIcon className="is-size-5" icon={faFilePdf} />) : null}

                            <Controller
                              name={`checkEntry.${index}.check_image`}
                              control={control}
                              rules={{ required: true }}
                              render={(field) => (
                                buildDropzone(field, `checkEntry.${index}.check_image`)
                              )}
                            />

                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.check_image && (errors.checkEntry[index].check_image.message || 'Check image is required')}
                            </p>
                          </td>
                          <td>
                            {/*eslint-disable-next-line no-nested-ternary*/}
                            {checkEntryWatch?.[index]?.envelope_image ?

                              (checkEntryWatch?.[index]?.envelope_image.startsWith('data:image') ? (
                                <img
                                  style={{ width: "30px", height: "30px" }}
                                  src={checkEntryWatch?.[index]?.envelope_image}
                                />
                              ) : checkEntryWatch?.[index]?.envelope_image.startsWith('http') ? (
                                <div style={{ paddingBottom: '0.2rem' }}>
                                  <a
                                    className="button is-primary"
                                    href={checkEntryWatch?.[index]?.envelope_image}
                                    style={{
                                      height: '2rem',
                                      width: '2rem',
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </a>
                                </div>
                              ) :
                                <FontAwesomeIcon className="is-size-5" icon={faFilePdf} />) : null}
                            <Controller
                              name={`checkEntry.${index}.envelope_image`}
                              control={control}
                              rules={{ required: false }}
                              render={(field) => (
                                buildDropzone(field, `checkEntry.${index}.envelope_image`)
                              )}
                            />
                            <p
                              className="has-text-danger is-size-7 lox-capitalize-first-letter"
                            >
                              {errors?.checkEntry?.[index]?.envelope_image && (errors.checkEntry[index].envelope_image.message || 'Envelope image error')}
                            </p>
                          </td>
                          <td>
                            <div>
                              {(lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || batchSaving) ? null : (
                                <button
                                  type="button"
                                  disabled={(rowSaving.length > 0)}
                                  className="button is-link has-text-white header-button px-4 mt-1"
                                  style={{ width: "100px", height: "30px" }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    saveRow(index);
                                  }}
                                > {(rowSaving.indexOf(index) >= 0) ? <Loading /> : 'Save'}
                                </button>
                              )}

                              {(!lockedRows.includes(checkEntryWatch[index]?.payment_check_entry) || batchSaving) ? null : (
                                <span
                                  className={`has-text-white is-link is-clickable mt-1 ml-1`}
                                  style={{ display: 'inline-block' }}
                                >
                                  <div
                                    className={`is-flex is-justify-content-center has-background-warning is-align-items-center`}
                                    style={{ borderRadius: "5px", width: "29px", height: "29px", margin: "0 auto" }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      updateLockRows(checkEntryWatch[index]?.payment_check_entry);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </div>
                                </span>
                              )}

                              <span
                                className={`has-text-white is-link ${canPerformRowAction() ? 'is-clickable' : ''} mt-1 ml-1`}
                                style={{ display: 'inline-block', ...(!canPerformRowAction() && { cursor: "not-allowed" }) }}
                              >
                                {(rowDeleting.indexOf(checkEntryWatch[index]?.payment_check_entry) >= 0) ? <Loading /> : (
                                  <div
                                    className={`is-flex is-justify-content-center ${(rowSaving.indexOf(index) >= 0 || !canPerformRowAction()) ? 'has-background-grey' : 'has-background-danger'} is-align-items-center`}
                                    style={{ borderRadius: "5px", width: "29px", height: "29px", margin: "0 auto" }}
                                    onClick={() => canPerformRowAction() && handleRowDelete(index, checkEntryWatch[index]?.payment_check_entry)}
                                  >
                                    <FontAwesomeIcon icon={faTrashCan} />

                                  </div>
                                )}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                    <tr>
                      <td colSpan="10" className="has-text-right">
                        <button
                          type="button"
                          className="button has-background-grey-light header-button px-4"
                          onClick={(e) => {
                            e.preventDefault();
                            append(defaultCheckEntryValues)
                          }}
                        > + Add
                        </button>
                      </td>
                    </tr>
                  </tbody>

                </table>
              </div>
              <div className="pt-4 mb-6 is-flex is-justify-content-end">

              </div>

              <div className="is-fullwidth margin-top-15 is-size-5 has-background-grey">
                <div className="p-2 pb-4">
                  Check Batch Information
                </div>
                <div className="has-background-light px-3 columns">
                  <div
                    className="column is-2 is-flex is-justify-content-center is-narrow has-text-weight-bold is-size-7"
                  >
                    Deposit Slip Total $ <span className="has-text-danger">*</span>
                  </div>
                  <div
                    className="column is-2 is-flex is-justify-content-center is-narrow has-text-weight-bold is-size-7"
                  >
                    Deposit Date $ <span className="has-text-danger">*</span>
                  </div>
                  <div
                    className="column is-1  is-flex is-justify-content-center is-narrow has-text-weight-bold is-size-7"
                  >
                    Batch Image
                  </div>
                  <div
                    className="column is-1  is-flex is-justify-content-center is-narrow has-text-weight-bold is-size-7"
                  >
                    Deposit Slip <span className="has-text-danger">*</span>
                  </div>
                </div>
                <div className="columns has-background-light mx-auto px-3">
                  <div
                    className="column is-2 is-justify-content-center is-narrow is-size-7"
                  >
                    <input
                      className="input is-small"
                      type="number"
                      step="0.01"
                      {...register(`deposit_slip_amount`, {
                        required: true
                      })}
                      onBlur={() => { clearErrors(`deposit_slip_amount`) }}
                    />
                    <p
                      className="has-text-danger is-size-7 lox-capitalize-first-letter"
                    >
                      {errors?.deposit_slip_amount && (errors.deposit_slip_amount.message || 'Deposit slip amount is required')}
                    </p>
                  </div>

                  <div
                    className="column is-2 is-justify-content-center is-narrow is-size-7"
                  >
                    <input
                      className="input is-small"
                      type="date"
                      {...register(`deposit_date`, {
                        required: true
                      })}
                      onBlur={() => { clearErrors(`deposit_date`) }}
                    />
                    <p
                      className="has-text-danger is-size-7 lox-capitalize-first-letter"
                    >
                      {errors?.deposit_date && (errors.deposit_date.message || 'Deposit date is required')}
                    </p>
                  </div>

                  <div className="column is-1 has-text-centered is-justify-content-center">
                    {/*eslint-disable-next-line no-nested-ternary*/}
                    {batchImageWatch ?

                      (batchImageWatch.startsWith('data:image') ? (
                        <img
                          style={{ width: "30px", height: "30px" }}
                          src={batchImageWatch}
                        />
                      ) : batchImageWatch.startsWith('http') ? (
                        <div style={{ paddingBottom: '0.2rem' }}>
                          <a
                            className="button is-primary"
                            href={batchImageWatch}
                            style={{
                              height: '2rem',
                              width: '2rem',
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </a>
                        </div>
                      ) :
                        <FontAwesomeIcon className="is-size-5" icon={faFilePdf} />) : null}

                    <Controller
                      name="batch_image"
                      control={control}
                      render={(field) => (
                        buildDropzone(field, `batch_image`, true)
                      )}
                    />
                    <p
                      className="has-text-danger is-size-7 lox-capitalize-first-letter"
                    >
                      {errors?.batch_image?.message ?? ''}
                    </p>
                  </div>
                  <div className="column is-1 has-text-centered is-justify-content-center">
                    {/*eslint-disable-next-line no-nested-ternary*/}
                    {slipImageWatch ?

                      (slipImageWatch.startsWith('data:image') ? (
                        <img
                          style={{ width: "30px", height: "30px" }}
                          src={slipImageWatch}
                        />
                      ) : slipImageWatch.startsWith('http') ? (
                        <div style={{ paddingBottom: '0.2rem' }}>
                          <a
                            className="button is-primary"
                            href={slipImageWatch}
                            style={{
                              height: '2rem',
                              width: '2rem',
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </a>
                        </div>
                      ) :
                        <FontAwesomeIcon className="is-size-5" icon={faFilePdf} />) : null}

                    <Controller
                      name="slip_image"
                      control={control}
                      render={(field) => (
                        buildDropzone(field, `slip_image`, true)
                      )}
                    />
                    <p
                      className="has-text-danger is-size-7 lox-capitalize-first-letter"
                    >
                      {errors?.slip_image && (errors.slip_image.message || 'Slip image is required')}
                    </p>

                  </div>
                  <div
                    className="column is-3 is-flex is-justify-content-center is-narrow has-text-weight-bold is-size-7"
                  >
                    {
                      isVerified ? (
                        <>
                          <button
                            className="button is-size-6 button is-link has-text-white header-button py-0 mr-1"
                            type="submit"
                            disabled={batchSaving}
                            onClick={(e) => { setProcessingStatusId(processingStatusVerifyDDOId) }}
                          >
                            <span className="mx-3">
                              Save as Draft
                              <span
                                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                                data-tooltip={'Save the batch as a draft to continue editing later. The batch will not be submitted for processing yet.'}
                              >
                                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-white" />
                              </span>
                            </span>
                          </button>

                          <button
                            className="button is-size-6 button is-primary has-text-white header-button py-0"
                            type="submit"
                            disabled={batchSaving}
                            onClick={(e) => { setProcessingStatusId(processingStatusPendingDDOId) }}
                          >
                            <span className="mx-3">
                              Finalize
                              <span
                                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                                data-tooltip={'Finalize and submit the batch for processing. No further edits will be possible after finalization.'}
                              >
                                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-white" />
                              </span>
                            </span>
                          </button>
                        </>
                      ) : (
                        <button
                          className="button is-size-6 has-background-grey has-text-white py-0"
                          type="button"
                          onClick={(e) => { e.preventDefault(); handleVerify() }}
                        >
                          <span className="mx-3">
                            Verify
                            <span
                              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                              data-tooltip={'Verify the batch to ensure all entries are complete and the total amount matches the deposit slip. This step must be completed before saving or finalizing the batch.'}
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-white" />
                            </span>
                          </span>
                        </button>
                      )
                    }

                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      />
    )
  } else if (!paymentCheckBatchId) {
    return (
      <div className="sections" style={{ height: "100%" }}>

        <div className="columns is-flex is-vcentered is-centered is-fullheight" style={{ height: "80%" }}>
          <div className="column is-half has-background-light p-0">
            <div
              className="p-2 portfolio-select-header is-vcentered is-flex has-text-black modal-card-head-background-color has-text-weight-bold"
            >
              Select Portfolio
            </div>
            <div className="columns is-centered is-vcentered is-fullheight" style={{ height: "160px" }}>
              <div className="column is-three-quarters columns">
                <div className="column is-three-quarters">

                  <Select
                    options={validPortfolios.map((p, index) => ({
                      label: p.name,
                      value: index
                    }))}
                    isSearchable={false}
                    onChange={(val) => {
                      setCurrentDropdownSelection(val.value)
                    }}
                    styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                  />
                </div>
                <div className="column">

                  <button
                    disabled={currentDropdownSelection == null}
                    type="button"
                    className="button is-link has-text-white header-button px-4"
                    onClick={handleSelectPortfolio}
                  >Select
                  </button>
                </div>
              </div>
            </div>
            <div className="portfolio-select-header">
            </div>

          </div>
        </div>
      </div>
    )
  } else {
    return <Loading />
  }
}

export default BulkCheckEntry
